import { Info, Link2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import { Button } from "../../../../components/custom/custom-button";
import CurrencyText from "../../../../components/fragment/cur-text/CurrencyText";
import { RazorNumberFormat } from "../../../../components/razor-reuse";
import {
  getReferralLinkExpiryStats,
  getReferralSettings,
  updateReferralSettings,
} from "../../../../redux/referral";
import RootState, {
  ReferralLinkExpiryStats,
  UpdateReferralSettingsPayload,
} from "../../../../redux/type";
import ExpiryConfigCard, {
  DEFAULT_EXPIRY_CONFIG,
  getExpiryConfigSummary,
  type ExpiryConfigValue,
} from "../components/ExpiryConfigCard";
import QualifyingThresholdTable, {
  type QualifyingThresholdValue,
} from "../components/QualifyingThresholdTable";
import RewardAmountEditor from "../components/RewardAmountEditor";

interface RewardAmounts {
  referral: number;
  referee: number;
}

const REWARD_CURRENCIES = [
  { code: "USD", symbol: "$" },
  { code: "GBP", symbol: "£" },
  { code: "CAD", symbol: "CA$" },
  { code: "EUR", symbol: "€" },
  { code: "NGN", symbol: "₦" },
];

const LINK_EXPIRY_STAT_CONFIG: {
  key: keyof ReferralLinkExpiryStats;
  label: string;
}[] = [
  { key: "expiring_in_7_days", label: "Expiring in 7 days" },
  { key: "expiring_this_month", label: "Expiring this month" },
  { key: "expired", label: "Expired" },
];

function buildReferralSettingsPayload(
  qualificationThresholds: Record<string, QualifyingThresholdValue>,
  rewardAmounts: Record<string, RewardAmounts>,
  expiryConfig: ExpiryConfigValue,
  currencies: any[],
): UpdateReferralSettingsPayload {
  return {
    qualifying_thresholds: (currencies || [])
      .filter((currency) => currency?.code)
      .map((currency) => {
        const value = qualificationThresholds[currency.code] || {
          referral: "",
          referee: "",
        };
        return {
          currency_code: currency.code,
          qualifying_referrer_amount: Number(value.referral) || 0,
          qualifying_referee_amount: Number(value.referee) || 0,
        };
      }),
    rewards: REWARD_CURRENCIES.map((currency) => {
      const value = rewardAmounts[currency.code] || { referral: 0, referee: 0 };
      return {
        currency_code: currency.code,
        referrer_reward_type: "fixed",
        referrer_reward_value: value.referral,
        referee_reward_type: "fixed",
        referee_reward_value: value.referee,
      };
    }),
    link_expiry_type:
      expiryConfig.type === "fixed-date"
        ? "fixed_date"
        : expiryConfig.type === "duration"
          ? "duration"
          : "none",
    link_expiry_duration_value:
      expiryConfig.type === "duration" ? expiryConfig.durationDays : null,
    link_expiry_duration_unit:
      expiryConfig.type === "duration" ? expiryConfig.durationUnit : null,
    link_expiry_fixed_date:
      expiryConfig.type === "fixed-date" && expiryConfig.fixedDate
        ? `${expiryConfig.fixedDate}T00:00:00.000Z`
        : null,
  };
}

export default function ReferralSettings() {
  const dispatch = useDispatch();
  const {
    link_expiry_stats,
    loading_link_expiry_stats,
    referral_settings,
    loading_settings,
    loading_settings_update,
  } = useSelector((state: RootState) => state.referral);
  const { all_currencies_drop } = useSelector(
    (state: RootState) => state.settings,
  );
  const { user } = useSelector((state: RootState) => state.auth);
  const canEditReferralSetting = !!user?.permissions?.includes(
    "edit_referral_setting",
  );

  const [qualificationThresholds, setQualificationThresholds] = useState<
    Record<string, QualifyingThresholdValue>
  >({});
  const [rewardAmounts, setRewardAmounts] = useState<
    Record<string, RewardAmounts>
  >(() =>
    Object.fromEntries(
      REWARD_CURRENCIES.map((c) => [c.code, { referral: 0, referee: 0 }]),
    ),
  );
  const [expiryConfig, setExpiryConfig] = useState<ExpiryConfigValue>(
    DEFAULT_EXPIRY_CONFIG,
  );
  const [baselinePayload, setBaselinePayload] = useState<string | null>(null);
  // Whether the settings fetch has settled — separate from `referral_settings`
  // itself, since a brand-new account legitimately has no settings yet (the
  // response never populates `referral_settings`), and we still need to
  // capture an (empty/default) baseline in that case so edits are detected.
  const [settingsFetchSettled, setSettingsFetchSettled] = useState(false);

  useEffect(() => {
    dispatch(getReferralLinkExpiryStats() as any);
    (dispatch(getReferralSettings() as any) as Promise<any>).finally(() =>
      setSettingsFetchSettled(true),
    );
  }, [dispatch]);

  useEffect(() => {
    if (!settingsFetchSettled || !all_currencies_drop?.length) return;

    const nextQualificationThresholds = Object.fromEntries(
      (referral_settings?.qualifying_thresholds || [])
        .filter((threshold) => threshold?.currency_code)
        .map((threshold) => [
          threshold.currency_code,
          {
            referral: String(threshold.qualifying_referrer_amount ?? 0),
            referee: String(threshold.qualifying_referee_amount ?? 0),
          },
        ]),
    );

    const nextRewardAmounts = Object.fromEntries(
      REWARD_CURRENCIES.map((currency) => {
        const reward = referral_settings?.rewards?.find(
          (r) => r?.currency_code === currency.code,
        );
        return [
          currency.code,
          {
            referral: reward?.referrer_reward_value || 0,
            referee: reward?.referee_reward_value || 0,
          },
        ];
      }),
    );

    const nextExpiryConfig: ExpiryConfigValue =
      referral_settings?.link_expiry_type === "fixed_date"
        ? {
            type: "fixed-date",
            durationDays: DEFAULT_EXPIRY_CONFIG.durationDays,
            durationUnit: DEFAULT_EXPIRY_CONFIG.durationUnit,
            fixedDate:
              referral_settings.link_expiry_fixed_date?.split("T")[0] || "",
          }
        : referral_settings?.link_expiry_type === "duration"
          ? {
              type: "duration",
              durationDays:
                referral_settings.link_expiry_duration_value ||
                DEFAULT_EXPIRY_CONFIG.durationDays,
              durationUnit:
                referral_settings.link_expiry_duration_unit ||
                DEFAULT_EXPIRY_CONFIG.durationUnit,
              fixedDate: "",
            }
          : DEFAULT_EXPIRY_CONFIG;

    setQualificationThresholds(nextQualificationThresholds);
    setRewardAmounts(nextRewardAmounts);
    setExpiryConfig(nextExpiryConfig);
    setBaselinePayload(
      JSON.stringify(
        buildReferralSettingsPayload(
          nextQualificationThresholds,
          nextRewardAmounts,
          nextExpiryConfig,
          all_currencies_drop,
        ),
      ),
    );
  }, [settingsFetchSettled, referral_settings, all_currencies_drop]);

  const currentPayload = buildReferralSettingsPayload(
    qualificationThresholds,
    rewardAmounts,
    expiryConfig,
    all_currencies_drop || [],
  );
  const hasUnsavedChanges =
    baselinePayload !== null &&
    JSON.stringify(currentPayload) !== baselinePayload;

  const handleSaveSettings = () => {
    dispatch(updateReferralSettings(currentPayload) as any);
  };

  const isLinkExpiryStatsLoading =
    loading_link_expiry_stats && !link_expiry_stats;
  const isInitialLoad = loading_settings && !referral_settings;

  const updateQualificationThreshold = (
    code: string,
    field: keyof QualifyingThresholdValue,
    nextValue: string,
  ) => {
    setQualificationThresholds((prev) => {
      const current = prev[code] || { referral: "", referee: "" };
      return {
        ...prev,
        [code]: { ...current, [field]: nextValue },
      };
    });
  };

  const updateRewardAmount = (
    code: string,
    field: keyof RewardAmounts,
    nextValue: number,
  ) => {
    setRewardAmounts((prev) => ({
      ...prev,
      [code]: { ...prev[code], [field]: nextValue },
    }));
  };

  if (isInitialLoad) {
    return (
      <div className="py-[16px] space-y-[20px]">
        <div>
          <p className="text-[20px] font-bold text-[#1E1E1E]">
            Referral Program Settings
          </p>
          <p className="text-[14px] text-[#758089] mt-[4px]">
            Configure qualification thresholds, expiry timelines and reward
            amount
          </p>
        </div>
        <Skeleton height={220} />
        <Skeleton height={260} />
        <Skeleton height={160} />
        <Skeleton height={140} />
      </div>
    );
  }

  return (
    <div className="py-[16px] space-y-[20px]">
      {/* header row */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[20px] font-bold text-[#1E1E1E]">
            Referral Program Settings
          </p>
          <p className="text-[14px] text-[#758089] mt-[4px]">
            Configure qualification thresholds, expiry timelines and reward
            amount
          </p>
        </div>
        {canEditReferralSetting && (
          <Button
            disabled={!hasUnsavedChanges}
            isLoading={loading_settings_update}
            onClick={handleSaveSettings}
          >
            Save Settings
          </Button>
        )}
      </div>

      {/* qualification threshold */}
      <div className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[16px] font-bold text-[#1E1E1E]">
              Qualification Threshold
            </p>
            <p className="text-[13px] text-[#758089] mt-[2px]">
              The minimum amount a referred user must complete to qualify the
              referral
            </p>
          </div>
          <span className="inline-flex items-center gap-[6px] rounded-[999px] bg-[#F4F4F5] px-[12px] py-[6px] text-[13px] text-[#3F3F46]">
            <Info className="size-[14px]" />
            Minimum transaction amount
          </span>
        </div>

        <QualifyingThresholdTable
          currencies={all_currencies_drop || []}
          values={qualificationThresholds}
          onChange={updateQualificationThreshold}
        />
      </div>

      {/* rewards amount by currency */}
      <div className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
        <p className="text-[16px] font-bold text-[#1E1E1E]">
          Rewards Amount by Currency
        </p>
        <p className="text-[13px] text-[#758089] mt-[2px]">
          Set the reward amount paid to the referrer per qualified referral, per
          currency
        </p>

        <div className="mt-[16px]">
          {REWARD_CURRENCIES.map((currency) => (
            <div
              key={currency.code}
              className="flex flex-wrap items-center justify-between gap-[16px] border-b border-[#F0F0F0] py-[16px] last:border-b-0"
            >
              <div className="flex items-center gap-[12px]">
                <CurrencyText currency={currency.code} imgSize="32px" />
                <div>
                  <p className="text-[14px] font-bold text-[#1E1E1E]">
                    {currency.code}
                  </p>
                  <p className="text-[13px] text-[#758089]">
                    Reward per qualified referral
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-[32px]">
                <RewardAmountEditor
                  label="Referrer Reward"
                  symbol={currency.symbol}
                  value={rewardAmounts[currency.code].referral}
                  onChange={(next) =>
                    updateRewardAmount(currency.code, "referral", next)
                  }
                />
                <RewardAmountEditor
                  label="Referee Reward"
                  symbol={currency.symbol}
                  value={rewardAmounts[currency.code].referee}
                  onChange={(next) =>
                    updateRewardAmount(currency.code, "referee", next)
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* referral code/link expiry configuration */}
      <ExpiryConfigCard value={expiryConfig} onChange={setExpiryConfig} />

      {/* existing links */}
      <div className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
        <p className="text-[16px] font-bold text-[#1E1E1E]">Existing Links</p>
        <p className="text-[13px] text-[#758089] mt-[2px]">
          Changes to expiry settings only apply to newly generated links. Links
          already shared will continue to follow their original expiry rules
        </p>
        <div className="mt-[20px] grid grid-cols-1 sm:grid-cols-3 gap-[16px]">
          {LINK_EXPIRY_STAT_CONFIG.map((stat) => (
            <div
              key={stat.key}
              className="rounded-[8px] bg-[#F4F4F5] p-[16px] text-center"
            >
              {isLinkExpiryStatsLoading ? (
                <Skeleton width={60} height={20} style={{ margin: "0 auto" }} />
              ) : (
                <p className="text-[20px] font-bold text-[#1E1E1E]">
                  {RazorNumberFormat(link_expiry_stats?.[stat.key] || 0, {
                    hideDecimal: true,
                    hideSymbol: true,
                  })}
                </p>
              )}
              <p className="text-[13px] text-[#758089] mt-[4px]">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* current configuration summary */}
      <div className="flex items-start gap-[10px] rounded-[8px] bg-[#F4EDFB] px-[16px] py-[12px]">
        <Link2 className="size-[16px] text-[#734792] mt-[2px]" />
        <div>
          <p className="text-[13px] font-medium text-[#734792]">
            Current configuration
          </p>
          <p className="text-[13px] text-[#3F3F46] mt-[2px]">
            {getExpiryConfigSummary(expiryConfig)}
          </p>
        </div>
      </div>
    </div>
  );
}
